import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { register } from '../api/auth';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { HiOutlineClipboardDocument, HiOutlineCheckCircle, HiOutlineArrowRight } from 'react-icons/hi2';

export default function Register() {
  const [form, setForm] = useState({ company_name: '', owner_name: '', email: '', password: '', mobile: '' });
  const [loading, setLoading] = useState(false);
  const [apiKey, setApiKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const { loginUser } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await register(form);
      loginUser(res.data.data);
      if (res.data.data.api_key) {
        setApiKey(res.data.data.api_key);
      } else {
        toast.success('Account created!');
        navigate('/conversations');
      }
    } catch (err) {
      const errors = err.response?.data?.errors;
      if (errors?.length) {
        errors.forEach((e) => toast.error(e.message));
      } else {
        toast.error(err.response?.data?.message || 'Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // API key reveal screen
  if (apiKey) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-8">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl shadow-sm border border-dark-200 p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-green-50 flex items-center justify-center">
                <HiOutlineCheckCircle className="w-9 h-9 text-green-500" />
              </div>
              <h2 className="text-xl font-bold text-dark-800">Account Created!</h2>
              <p className="text-sm text-dark-400 mt-1">Save your API key — it won't be shown again</p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5">
              <p className="text-xs font-semibold text-amber-700 mb-3">Your Default API Key</p>
              <div className="flex items-center gap-2 bg-white border border-amber-200 rounded-lg px-3 py-2.5">
                <code className="flex-1 text-xs font-mono text-dark-700 break-all">{apiKey}</code>
                <button onClick={handleCopy}
                  className="flex-shrink-0 flex items-center gap-1 px-2 py-1 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors">
                  {copied
                    ? <HiOutlineCheckCircle className="w-4 h-4 text-green-600" />
                    : <HiOutlineClipboardDocument className="w-4 h-4 text-amber-700" />}
                  <span className="text-xs font-medium text-amber-700">{copied ? 'Copied!' : 'Copy'}</span>
                </button>
              </div>
              <p className="text-xs text-amber-600 mt-2">
                Use this as the <code className="bg-amber-100 px-1 rounded">x-api-key</code> header in all API requests.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mb-5 text-xs text-dark-500 space-y-1">
              <p className="font-semibold text-dark-700 mb-2">What to do next:</p>
              <p>1. Copy and save the key above somewhere safe</p>
              <p>2. You can generate more keys from <strong>API Keys</strong> in the dashboard</p>
              <p>3. Read how to use it in <strong>API Docs</strong></p>
            </div>

            <button onClick={() => navigate('/conversations')}
              className="w-full flex items-center justify-center gap-2 bg-primary-500 text-white py-3 rounded-xl font-semibold text-sm hover:bg-primary-600 transition-colors">
              Go to Dashboard <HiOutlineArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary-500">WhatsApp SaaS</h1>
          <p className="text-dark-400 mt-2">Create your organization account</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-dark-200 p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-dark-700 mb-1">Company Name</label>
            <input type="text" required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
              className="w-full px-3 py-2.5 border border-dark-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none text-sm"
              placeholder="Your Company" />
          </div>
          <div>
            <label className="block text-sm font-medium text-dark-700 mb-1">Your Name</label>
            <input type="text" required value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
              className="w-full px-3 py-2.5 border border-dark-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none text-sm"
              placeholder="Full name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-dark-700 mb-1">Email</label>
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-3 py-2.5 border border-dark-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none text-sm"
              placeholder="you@example.com" />
          </div>
          <div>
            <label className="block text-sm font-medium text-dark-700 mb-1">Phone (optional)</label>
            <input type="text" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })}
              className="w-full px-3 py-2.5 border border-dark-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none text-sm"
              placeholder="+923001234567" />
          </div>
          <div>
            <label className="block text-sm font-medium text-dark-700 mb-1">Password</label>
            <input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full px-3 py-2.5 border border-dark-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none text-sm"
              placeholder="Min 8 chars, uppercase, lowercase, number" />
          </div>

          <button type="submit" disabled={loading}
            className="w-full bg-primary-500 text-white py-2.5 rounded-lg font-medium hover:bg-primary-600 disabled:opacity-50 transition-colors text-sm">
            {loading ? 'Creating account...' : 'Create Account'}
          </button>

          <p className="text-center text-sm text-dark-400">
            Already have an account? <Link to="/login" className="text-primary-500 hover:text-primary-600 font-medium">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
